import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { auth } from '../../firebase';
import { Responder, MutualRespectPair } from '../../types/respect';
import { ProfileCircle } from './ProfileCircle';

interface ConnectionStatus {
  isConnected: boolean;
  isBekende: boolean;
  bekendVia?: string; // Naam van de mutual connection
}

interface RespectHoverCardProps {
  responders: Responder[];
  mutualPairs: MutualRespectPair[];
  isVisible: boolean;
  postId: string;
  className?: string;
  onConnect?: (userId: string, userName: string) => void;
}

/**
 * RespectHoverCard Component
 * Toont 2 kolommen: Respect lijst + Mutual Respect lijst
 * Per persoon: Verbonden / Bekende van X / Verbinden knop
 */
export const RespectHoverCard: React.FC<RespectHoverCardProps> = ({
  responders,
  mutualPairs,
  isVisible,
  postId,
  className = '',
  onConnect
}) => {
  const [connectionStatuses, setConnectionStatuses] = useState<Map<string, ConnectionStatus>>(new Map());
  const [loadingStatuses, setLoadingStatuses] = useState<boolean>(false);
  const currentUserId = auth.currentUser?.uid;

  // Laad connection statuses voor alle users
  useEffect(() => {
    if (!isVisible || !currentUserId) return;

    const loadConnectionStatuses = async () => {
      setLoadingStatuses(true);
      const statuses = new Map<string, ConnectionStatus>();
      
      // Verzamel alle unieke user IDs
      const allUserIds = new Set<string>();
      responders.forEach(r => allUserIds.add(r.id));
      mutualPairs.forEach(pair => {
        allUserIds.add(pair.userA.id);
        allUserIds.add(pair.userB.id);
      });

      // Import connectionRequestService dynamisch
      try {
        const { connectionRequestService } = await import('../../services/connectionRequestService');
        
        for (const userId of Array.from(allUserIds)) {
          if (userId === currentUserId) continue;
          
          // Check directe connectie
          const isConnected = await checkIsConnected(userId);
          
          // Check 2e graads connectie (bekende)
          let isBekende = false;
          let bekendVia: string | undefined;
          
          if (!isConnected) {
            const secondDegree = await check2ndDegreeConnection(userId);
            isBekende = secondDegree.isBekende;
            bekendVia = secondDegree.bekendVia;
          }
          
          statuses.set(userId, { isConnected, isBekende, bekendVia });
        }
      } catch (error) {
        console.error('Error loading connection statuses:', error);
      }
      
      setConnectionStatuses(statuses);
      setLoadingStatuses(false);
    };

    loadConnectionStatuses();
  }, [isVisible, currentUserId, responders, mutualPairs]);

  // Check of user direct connected is
  const checkIsConnected = async (targetUserId: string): Promise<boolean> => {
    if (!currentUserId) return false;
    
    try {
      const { getDocs, query, collection, where } = await import('firebase/firestore');
      const { db } = await import('../../firebase');
      
      // Check connections collection
      const q1 = query(
        collection(db, 'connections'),
        where('user1Id', '==', currentUserId),
        where('user2Id', '==', targetUserId)
      );
      const q2 = query(
        collection(db, 'connections'),
        where('user1Id', '==', targetUserId),
        where('user2Id', '==', currentUserId)
      );
      
      const [snap1, snap2] = await Promise.all([getDocs(q1), getDocs(q2)]);
      if (!snap1.empty || !snap2.empty) return true;
      
      // Check accepted connectionRequests
      const q3 = query(
        collection(db, 'connectionRequests'),
        where('fromUserId', '==', currentUserId),
        where('toUserId', '==', targetUserId),
        where('status', '==', 'accepted')
      );
      const q4 = query(
        collection(db, 'connectionRequests'),
        where('fromUserId', '==', targetUserId),
        where('toUserId', '==', currentUserId),
        where('status', '==', 'accepted')
      );
      
      const [snap3, snap4] = await Promise.all([getDocs(q3), getDocs(q4)]);
      return !snap3.empty || !snap4.empty;
    } catch (error) {
      console.error('Error checking connection:', error);
      return false;
    }
  };

  // Check 2e graads connectie
  const check2ndDegreeConnection = async (targetUserId: string): Promise<{ isBekende: boolean; bekendVia?: string }> => {
    if (!currentUserId) return { isBekende: false };
    
    try {
      const { getDocs, query, collection, where, doc, getDoc } = await import('firebase/firestore');
      const { db } = await import('../../firebase');
      
      // Stap 1: Haal mijn directe connecties op
      const myConnections: string[] = [];
      
      const q1 = query(collection(db, 'connections'), where('user1Id', '==', currentUserId));
      const q2 = query(collection(db, 'connections'), where('user2Id', '==', currentUserId));
      
      const [snap1, snap2] = await Promise.all([getDocs(q1), getDocs(q2)]);
      
      snap1.docs.forEach(d => myConnections.push(d.data().user2Id));
      snap2.docs.forEach(d => myConnections.push(d.data().user1Id));
      
      // Stap 2: Check of target connected is met een van mijn connecties
      for (const myConnectionId of myConnections) {
        const c1 = query(
          collection(db, 'connections'),
          where('user1Id', '==', myConnectionId),
          where('user2Id', '==', targetUserId)
        );
        const c2 = query(
          collection(db, 'connections'),
          where('user1Id', '==', targetUserId),
          where('user2Id', '==', myConnectionId)
        );
        
        const [cs1, cs2] = await Promise.all([getDocs(c1), getDocs(c2)]);
        
        if (!cs1.empty || !cs2.empty) {
          // Haal naam van de mutual connection
          const profileDoc = await getDoc(doc(db, 'userProfiles', myConnectionId));
          const displayName = profileDoc.exists() ? profileDoc.data().displayName || 'Iemand' : 'Iemand';
          return { isBekende: true, bekendVia: displayName };
        }
      }
      
      return { isBekende: false };
    } catch (error) {
      console.error('Error checking 2nd degree connection:', error);
      return { isBekende: false };
    }
  };

  // Trigger bestaande connection modal
  const handleConnect = (userId: string, userName: string) => {
    if (onConnect) {
      onConnect(userId, userName);
    } else {
      // Fallback: trigger custom event dat CommunityDashboard oppakt
      window.dispatchEvent(new CustomEvent('openConnectionModal', {
        detail: { userId, userName }
      }));
    }
  };

  if (!isVisible) {
    return null;
  }

  const fallbackUrl = (name: string) => 
    `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'U')}&background=cccccc&color=fff&size=64`;

  // Render een persoon met status
  const renderPerson = (user: Responder, showMutualArrow: boolean = false, mutualWith?: Responder) => {
    const status = connectionStatuses.get(user.id);
    const isCurrentUser = user.id === currentUserId;
    
    return (
      <div key={user.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <ProfileCircle
            photoURL={user.photoURL || fallbackUrl(user.displayName)}
            displayName={user.displayName}
            className="w-8 h-8"
            isSuperKonnected={user.isSuperKonnected}
          />
          <div className="flex-1 min-w-0">
            <span className="text-sm text-gray-800 block truncate">
              {showMutualArrow && mutualWith ? (
                <>{user.displayName} <span className="text-gray-400">↔</span> {mutualWith.displayName}</>
              ) : (
                user.displayName
              )}
            </span>
            {isCurrentUser && (
              <span className="text-xs text-gray-400">Jij</span>
            )}
          </div>
        </div>
        
        {/* Connection status */}
        {!isCurrentUser && (
          <div className="ml-2 flex-shrink-0">
            {loadingStatuses ? (
              <span className="text-xs text-gray-400">...</span>
            ) : status?.isConnected ? (
              <span className="text-xs text-green-600 font-medium">✓ Verbonden</span>
            ) : status?.isBekende ? (
              <div className="text-right">
                <span className="text-xs text-amber-700 block">○ Bekende</span>
                <span className="text-xs text-gray-400">via {status.bekendVia}</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleConnect(user.id, user.displayName);
                  }}
                  className="text-xs text-blue-600 hover:underline block mt-0.5"
                >
                  Verbinden?
                </button>
              </div>
            ) : (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleConnect(user.id, user.displayName);
                }}
                className="text-xs text-blue-600 hover:text-blue-800 hover:underline px-2 py-1 rounded border border-blue-200 hover:bg-blue-50"
              >
                Verbinden
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  // Filter duplicaten uit mutualPairs voor weergave
  const uniqueMutualUsers = new Map<string, { user: Responder; mutualWith: Responder }>();
  mutualPairs.forEach(pair => {
    if (pair.userA.id !== currentUserId) {
      uniqueMutualUsers.set(pair.userA.id, { user: pair.userA, mutualWith: pair.userB });
    }
    if (pair.userB.id !== currentUserId) {
      uniqueMutualUsers.set(pair.userB.id, { user: pair.userB, mutualWith: pair.userA });
    }
  });

  const hasRespect = responders.length > 0;
  const hasMutual = uniqueMutualUsers.size > 0;

  // Portal naar body zodat de kaart boven alles zweeft
  const cardContent = (
    <div 
      className="fixed bg-white border border-gray-200 rounded-xl shadow-2xl"
      style={{ 
        minWidth: hasRespect && hasMutual ? '420px' : '240px',
        zIndex: 9999,
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        maxHeight: '80vh',
        overflowY: 'auto'
      }}
      role="dialog"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header met sluit knop */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-700">Interacties</h3>
        <button 
          onClick={(e) => {
            e.stopPropagation();
            // Trigger close via parent
            window.dispatchEvent(new CustomEvent('closeRespectCard'));
          }}
          className="text-gray-400 hover:text-gray-600 text-lg leading-none"
        >
          ×
        </button>
      </div>
      
      <div className={`flex ${hasRespect && hasMutual ? 'divide-x divide-gray-200' : ''}`}>
        {/* Respect kolom */}
        {hasRespect && (
          <div className={`p-4 ${hasMutual ? 'flex-1' : 'w-full'}`}>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
              Respect
            </h4>
            <div className="max-h-64 overflow-y-auto space-y-1">
              {responders.map(responder => renderPerson(responder))}
            </div>
          </div>
        )}
        
        {/* Mutual Respect kolom */}
        {hasMutual && (
          <div className={`p-4 ${hasRespect ? 'flex-1' : 'w-full'}`}>
            <h4 className="text-xs font-semibold text-red-500 uppercase tracking-wide mb-3">
              Mutual Respect
            </h4>
            <div className="max-h-64 overflow-y-auto space-y-1">
              {Array.from(uniqueMutualUsers.values()).map(({ user, mutualWith }) => 
                renderPerson(user, true, mutualWith)
              )}
            </div>
          </div>
        )}
        
        {/* Geen data */}
        {!hasRespect && !hasMutual && (
          <div className="p-6 text-center text-sm text-gray-500">
            Nog geen interacties op deze post
          </div>
        )}
      </div>
    </div>
  );

  // Backdrop om te sluiten bij klik erbuiten
  const backdrop = (
    <div 
      className="fixed inset-0 bg-black/20"
      style={{ zIndex: 9998 }}
      onClick={(e) => {
        e.stopPropagation();
        window.dispatchEvent(new CustomEvent('closeRespectCard'));
      }}
    />
  );

  return createPortal(
    <>
      {backdrop}
      {cardContent}
    </>,
    document.body
  );
};

export default RespectHoverCard;
